import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, ChevronDown, ChevronUp, TrendingUp, Users, Package, Loader2 } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from "recharts";

const PERIODS = [
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "12m", label: "12 meses" },
];

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const fmtCRC = (n) => `₡${Math.round(n || 0).toLocaleString("es-CR")}`;

const formatTrendLabel = (label, granularity) => {
  if (!label) return "";
  if (granularity === "month") {
    const [, m] = label.split("-");
    return MONTHS_ES[parseInt(m, 10) - 1] || label;
  }
  const [, m, d] = label.split("-");
  return `${d}/${m}`;
};

const BAR_COLORS = ["#2563eb", "#3b82f6", "#60a5fa", "#38bdf8", "#22d3ee", "#818cf8", "#a78bfa", "#f472b6", "#fb923c", "#fbbf24"];

export default function DashboardAnalytics({ boxId }) {
  const [expanded, setExpanded] = useState(false);
  const [period, setPeriod] = useState("7d");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    if (!boxId) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/inventory/analytics?box_id=${boxId}&period=${period}`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [boxId, period]);

  useEffect(() => {
    if (expanded) fetchAnalytics();
  }, [expanded, fetchAnalytics]);

  const trend = (data?.trend || []).map((t) => ({
    ...t,
    name: formatTrendLabel(t.label, data?.granularity),
  }));
  const topCustomers = data?.top_customers || [];
  const topProducts = data?.top_products || [];

  return (
    <div className="space-y-3" data-testid="dashboard-analytics-section">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-colors"
        data-testid="analytics-toggle-btn"
      >
        <span className="flex items-center gap-2 font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          <BarChart3 className="w-5 h-5" /> Analítica de ventas
        </span>
        {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {expanded && (
        <div className="space-y-4">
          {/* Period selector */}
          <div className="flex items-center gap-2">
            {PERIODS.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant={period === p.key ? "default" : "outline"}
                onClick={() => setPeriod(p.key)}
                data-testid={`analytics-period-${p.key}`}
              >
                {p.label}
              </Button>
            ))}
            {data?.totals && (
              <span className="ml-auto text-sm text-slate-600" data-testid="analytics-total-sales">
                Total: <b className="text-slate-900">{fmtCRC(data.totals.sales)}</b> · {data.totals.count} ventas
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500" data-testid="analytics-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando analítica...
            </div>
          ) : (
            <>
              {/* Sales trend */}
              <Card data-testid="analytics-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="w-4 h-4 text-blue-600" /> Tendencia de ventas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trend.length === 0 ? (
                    <p className="text-sm text-slate-500 py-8 text-center">Sin datos de ventas en este periodo.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `₡${(v / 1000).toLocaleString("es-CR")}k`} width={56} />
                        <Tooltip
                          formatter={(v, key) => (key === "total" ? [fmtCRC(v), "Ventas"] : [v, "Nº ventas"])}
                          labelStyle={{ color: "#0f172a", fontWeight: 600 }}
                          contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                        />
                        <Area type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} fill="url(#salesGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top customers by amount */}
                <Card data-testid="analytics-top-customers-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Users className="w-4 h-4 text-emerald-600" /> Mejores clientes (₡)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {topCustomers.length === 0 ? (
                      <p className="text-sm text-slate-500 py-8 text-center">Sin clientes con nombre en este periodo.</p>
                    ) : (
                      <div className="space-y-2" data-testid="top-customers-list">
                        {topCustomers.map((c, i) => {
                          const max = topCustomers[0].total || 1;
                          const pct = Math.max(4, (c.total / max) * 100);
                          return (
                            <div key={`${c.name}-${i}`} className="flex items-center gap-2" data-testid={`top-customer-${i}`}>
                              <span className="w-5 text-xs font-bold text-slate-400 text-right">{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-medium text-slate-700 truncate">{c.name}</span>
                                  <span className="text-sm font-semibold text-slate-900 whitespace-nowrap">{fmtCRC(c.total)}</span>
                                </div>
                                <div className="h-2 mt-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top products by quantity */}
                <Card data-testid="analytics-top-products-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Package className="w-4 h-4 text-indigo-600" /> Productos más vendidos (unidades)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {topProducts.length === 0 ? (
                      <p className="text-sm text-slate-500 py-8 text-center">Sin ventas de productos en este periodo.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={Math.max(180, topProducts.length * 34)}>
                        <BarChart data={topProducts} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={120} />
                          <Tooltip
                            formatter={(v) => [`${v} u`, "Unidades"]}
                            contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                          />
                          <Bar dataKey="quantity" radius={[0, 4, 4, 0]}>
                            {topProducts.map((_, i) => (
                              <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
